/* Grading periods — which columns a class is graded in (1st–4th Quarter,
 * Midterm/Final, …). Configured per Level (TEACHING department) in
 * Settings → Grading Periods; stored as `grades.period` codes. */

import API_BASE from "../config";

/** Built-in presets offered in Settings. `semesterNo` = which semester of
 *  the school year the period falls in; null = every semester. */
export const PRESETS = {
  // Pre-School / Elementary / Junior High: a subject runs the whole school
  // year, so one class carries all four quarters.
  quarters: {
    label: "Quarters — whole year (K–10)",
    periods: [
      { code: "Q1", label: "1st Quarter", semesterNo: null },
      { code: "Q2", label: "2nd Quarter", semesterNo: null },
      { code: "Q3", label: "3rd Quarter", semesterNo: null },
      { code: "Q4", label: "4th Quarter", semesterNo: null },
    ],
  },
  // Senior High: subjects change each semester — two quarters per semester.
  quartersSplit: {
    label: "Quarters — 2 per semester (Senior High)",
    periods: [
      { code: "Q1", label: "1st Quarter", semesterNo: 1 },
      { code: "Q2", label: "2nd Quarter", semesterNo: 1 },
      { code: "Q3", label: "3rd Quarter", semesterNo: 2 },
      { code: "Q4", label: "4th Quarter", semesterNo: 2 },
    ],
  },
  midtermFinal: {
    label: "College — Midterm / Final",
    periods: [
      { code: "MIDTERM", label: "Midterm", semesterNo: null },
      { code: "FINAL",   label: "Final",   semesterNo: null },
    ],
  },
  collegeFour: {
    label: "College — Prelim / Midterm / Pre-final / Final",
    periods: [
      { code: "PRELIM",   label: "Prelim",    semesterNo: null },
      { code: "MIDTERM",  label: "Midterm",   semesterNo: null },
      { code: "PREFINAL", label: "Pre-final", semesterNo: null },
      { code: "FINAL",    label: "Final",     semesterNo: null },
    ],
  },
};

/** What a Level uses until someone configures it: whole-year quarters,
 *  except Senior High (2 quarters per semester). */
export const DEFAULT_PERIODS = PRESETS.quarters.periods;
const SENIOR_RE = /senior|grade\s*1[12]\b|\bg1[12]\b/i;
export function defaultPeriodsFor(name) {
  return SENIOR_RE.test(name || "") ? PRESETS.quartersSplit.periods : PRESETS.quarters.periods;
}

/** Friendly names for codes that may appear in old grade rows even after a
 *  Level's list changes, so they never render as a raw code. */
const KNOWN_LABELS = {
  Q1: "1st Quarter", Q2: "2nd Quarter", Q3: "3rd Quarter", Q4: "4th Quarter",
  PRELIM: "Prelim", MIDTERM: "Midterm", PREFINAL: "Pre-final", FINAL: "Final",
};
export function periodLabel(code, periods = []) {
  return periods.find(p => p.code === code)?.label || KNOWN_LABELS[code] || code;
}

/** Fetch every configured period row (all Levels). Fails soft to []. */
export function fetchGradingPeriods() {
  return fetch(`${API_BASE}/api/grading-periods`)
    .then(r => (r.ok ? r.json() : []))
    .then(d => (Array.isArray(d) ? d : []))
    .catch(() => []);
}

/** The period list for one Level (configured rows, else the default for
 *  that level — `levelName` is the department or grade-level name). */
export function periodsForDepartment(rows, departmentId, levelName = "") {
  const mine = (rows || [])
    .filter(r => departmentId != null && String(r.departmentId) === String(departmentId))
    .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))
    .map(r => ({ code: r.code, label: r.label, semesterNo: r.semesterNo ?? null }));
  return mine.length ? mine : defaultPeriodsFor(levelName);
}

/** 1-based position of a semester within its school year: reads the
 *  leading number of the label ("1st", "2nd Semester"), else falls back to
 *  start-date / id order among that year's semesters. */
export function semesterNumber(semester, semesters = []) {
  if (!semester) return null;
  const m = String(semester.label || "").match(/\d+/);
  if (m) return Number(m[0]);
  const siblings = semesters
    .filter(x => String(x.schoolYearId) === String(semester.schoolYearId))
    .sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")) || a.id - b.id);
  const idx = siblings.findIndex(x => x.id === semester.id);
  return idx >= 0 ? idx + 1 : null;
}

/** Periods a class offering is graded in: its section's grade level →
 *  Level → period list, narrowed to the offering's semester. */
export function periodsForOffering({ offering, sections = [], gradeLevels = [], semesters = [], rows = [] }) {
  if (!offering) return DEFAULT_PERIODS;
  const sec = sections.find(x => x.id === offering.sectionId);
  const gl  = sec ? gradeLevels.find(g => g.id === sec.gradeLevelId) : null;
  const all = periodsForDepartment(rows, gl?.departmentId, gl?.name);
  const sem = semesters.find(x => x.id === offering.semesterId);
  const no  = semesterNumber(sem, semesters);
  const inSem = all.filter(p => p.semesterNo == null || no == null || p.semesterNo === no);
  // A semester with no matching periods (e.g. a 3rd-term label) shows the
  // whole list rather than an empty gradebook.
  return inSem.length ? inSem : all;
}

/** `periods` plus any extra codes found in existing grade rows (e.g. old
 *  MIDTERM/FINAL scores after switching to quarters), so nothing already
 *  encoded is ever hidden. */
export function withLegacyPeriods(periods, gradeRows) {
  const known = new Set(periods.map(p => p.code));
  const extra = [];
  for (const g of gradeRows || []) {
    if (g?.period && !known.has(g.period)) {
      known.add(g.period);
      extra.push({ code: g.period, label: periodLabel(g.period), semesterNo: null, legacy: true });
    }
  }
  return extra.length ? [...periods, ...extra] : periods;
}

/** Mean of the numeric scores in `{ [code]: score | {score} }`, or null. */
export function averageOf(cell, codes) {
  const vals = [];
  for (const c of codes || Object.keys(cell || {})) {
    const raw = cell?.[c];
    const v = raw != null && typeof raw === "object" ? raw.score : raw;
    if (v === "" || v == null) continue;
    const n = Number(v);
    if (Number.isFinite(n)) vals.push(n);
  }
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

/** DepEd descriptor for a 0–100 grade. */
export function descriptorFor(score) {
  if (score == null) return { label: "—", tone: "none" };
  if (score >= 90) return { label: "Outstanding",         tone: "high" };
  if (score >= 85) return { label: "Very Satisfactory",   tone: "high" };
  if (score >= 80) return { label: "Satisfactory",        tone: "high" };
  if (score >= 75) return { label: "Fairly Satisfactory", tone: "mid"  };
  return                  { label: "Did Not Meet Expectations", tone: "low" };
}
